import styles from './MeshViewer.module.css';
import { useState, useEffect, useRef, Suspense } from 'react';

import { Canvas } from '@react-three/fiber';
import { Environment, OrbitControls, useGLTF, PerspectiveCamera } from '@react-three/drei';
import ErrorBoundary from '../common/ErrorBoundary';
import LoadingCanvas from '../common/LoadingCanvas';
import ThreeDModel from '../common/ThreeDModel';

interface MeshViewerProps {
  signedUrl: string;
  selectedNames: string[];
  selectedPlaneNames: string[];
}

const MeshViewer = ({ signedUrl, selectedNames, selectedPlaneNames }: MeshViewerProps):
  React.JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const modelUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!signedUrl) return;

    const downloadGLB = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        setProgress(0);

        const response = await fetch(signedUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentLength = response.headers.get('Content-Length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let received = 0;

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          chunks.push(value);
          received += value.length;

          if (total > 0) {
            setProgress((received / total) * 100);
          }
        }

        // Combine chunks into a single Uint8Array
        const fullData = new Uint8Array(received);
        let position = 0;
        for (const chunk of chunks) {
          fullData.set(chunk, position);
          position += chunk.length;
        }

        // Create blob and object URL
        const blob = new Blob([fullData], { type: 'model/gltf-binary' });
        const objectUrl = URL.createObjectURL(blob);
        modelUrl.current = objectUrl;
        setProgress(100);

        // Small delay to show 100% completion
        setTimeout(() => {
          setIsLoading(false);
        }, 500);

      } catch (err) {
        console.error('Error downloading GLB:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        setIsLoading(false);
      }
    };

    downloadGLB();

    // Cleanup function to revoke object URL
    return (): void => {
      if (modelUrl.current) {
        URL.revokeObjectURL(modelUrl.current);
      }
    };
  }, [signedUrl]);

  if (error) {
    return (
      <div className={styles['canvas-container']}>
        <div className={`text-center ${styles['error-container']}`}>
          <div className={styles['error-symbol']}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <circle
                cx="12"
                cy="12"
                r="10.5"
                fill="#FFFFFF"
                stroke="#EF5350"
                strokeWidth="3"
              />
              <path
                d="M11 6h2v8h-2zm0 10h2v2h-2z"
                fill="#EF5350"
              />
            </svg>
          </div>
          <div className={styles['error-header']}>Spine Data Unavailable</div>
          <div>There was an unexpected error loading this spine segmentation. Please reload the page.</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles['canvas-container']}>
        <LoadingCanvas progress={progress} />
      </div>
    );
  }

  if (!modelUrl) {
    return (
      <div className={styles['canvas-container']}>
        <p className="text-gray-600">No model URL available</p>
      </div>
    );
  }

  return (
    <div className={styles['canvas-container']}>
      <ErrorBoundary fallback={<div>Error occurred.</div>}>
        <Canvas className={styles['spine-canvas']}>
          <PerspectiveCamera makeDefault position={[0, 0, 5]} fov={4}>
            {/* Light is now truly parented to the camera, with a small forward offset */}
            <pointLight
              position={[0, 0, 0]}  // in camera space (forward)
              intensity={4}
              distance={0}// infinite range
              decay={0}
              color="#E3E3E3"
            />
          </PerspectiveCamera>

          <Environment preset="studio" environmentIntensity={0.00} />

          {/* Key light from viewer's perspective (front-facing) */}
          <directionalLight
            position={[0, 0, 5]}
            intensity={0.55}
            color="#E3E3E3"
            castShadow={false}
          />

          {/* Fill light from slightly above and to the right */}
          <directionalLight
            position={[3, 5, 5]}
            intensity={0.3}
            color="#E3E3E3"
          />

          {/* Rim light from behind to add depth and separation */}
          <directionalLight
            position={[-5, 2, -5]}
            intensity={0.2}
            color="#E3E3E3"
          />

          <Suspense fallback={null}>

            {modelUrl.current && (
              <ThreeDModel
                url={modelUrl.current}
                highlightNames={selectedNames}
                visiblePlanes={selectedPlaneNames}
              />
            )}

          </Suspense>
          <OrbitControls
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            minDistance={1}
            maxDistance={8}
            enableDamping={true}
            dampingFactor={0.2}
            rotateSpeed={0.5}
            zoomSpeed={1.5}
          />
        </Canvas>
      </ErrorBoundary>
    </div>
  );
};

// Preload the GLB loader to improve performance
useGLTF.preload = (_url: string): void => {
  // This is handled automatically by drei
};

export default MeshViewer;
